import { Button, Modal } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AxiosError } from 'axios';
import { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PendingStory, VoiceStoryCapture } from 'src/Components/Memories/VoiceStoryCapture';
import { useAddMemoryRecording } from 'src/Hooks/Mutations/useMemoryMutations';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import { trackMemoryRecordingAdded } from 'src/Services/Mixpanel/events';

import styles from './VoiceStoryRecorder.module.scss';
import { Props } from './VoiceStoryRecorder.types';

const storyErrorMessage = (t: TFunction, error: unknown): string => {
  const response = (error as AxiosError<{ code?: string }>)?.response;
  switch (response?.data?.code) {
    case 'AUDIO_TOO_LARGE':
      return t(
        'memories.recorder.errors.tooLarge',
        'Ce récit est trop long. Faites-le en deux fois.'
      );
    case 'UNSUPPORTED_AUDIO_FORMAT':
      return t(
        'memories.recorder.errors.unsupported',
        "Ce navigateur enregistre dans un format qu'on ne sait pas lire. Essayez avec un autre navigateur."
      );
    default:
      return t(
        'memories.recorder.errors.generic',
        "Le récit n'a pas pu être envoyé. Vérifiez la connexion et réessayez."
      );
  }
};

/** "Ajouter un récit" on a memory: capture a story, then send it. */
const VoiceStoryRecorder = ({ memoryId, opened, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const { mutate: addRecording, isLoading: isSending } = useAddMemoryRecording(memoryId);
  const [story, setStory] = useState<PendingStory | null>(null);
  const [showMissingStory, setShowMissingStory] = useState(false);

  const handleClose = () => {
    setStory(null);
    setShowMissingStory(false);
    onClose();
  };

  const handleSend = () => {
    // Never a muted disabled button: say what is missing instead.
    if (!story) {
      setShowMissingStory(true);
      return;
    }
    addRecording(
      {
        audio: story.recording.audio,
        filename: story.recording.filename.replace('dictation', 'recit'),
        durationSeconds: story.recording.durationSeconds,
        speakerId: story.speakerId,
        speakerName: story.speakerName,
        language: i18n.language?.startsWith('en') ? 'en' : 'fr',
      },
      {
        onSuccess: () => {
          trackMemoryRecordingAdded({ duration_seconds: story.recording.durationSeconds });
          notifications.show({
            color: 'teal',
            message: t('memories.recorder.sent', 'Votre récit est dans le souvenir.'),
          });
          handleClose();
        },
        onError: sendError => {
          notifications.show({ color: 'red', message: storyErrorMessage(t, sendError) });
        },
      }
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('memories.recorder.modalTitle', 'Ajouter un récit')}
      size="lg"
      fullScreen={isMobile}
    >
      {/* Unmounted when closed: the microphone is released and the draft dropped. */}
      {opened && (
        <div className={styles.container}>
          <VoiceStoryCapture
            label={t('memories.form.voiceLabel', 'Récit à voix haute')}
            onChange={value => {
              setStory(value);
              setShowMissingStory(false);
            }}
          />
          {showMissingStory && (
            <div className={styles.error}>
              {t(
                'memories.recorder.storyMissing',
                'Enregistrez un récit et indiquez qui raconte avant de l’ajouter.'
              )}
            </div>
          )}
          <div className={styles.buttons}>
            <Button variant="outline" onClick={handleClose}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button onClick={handleSend} loading={isSending}>
              {t('memories.recorder.send', 'Ajouter ce récit')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default VoiceStoryRecorder;
